import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Wand2, ArrowRight, Clock, Mail, Sparkles, Flame, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (d - now) / 1000;
    if (diff < 0) return "now";
    if (diff < 3600) return `in ${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `in ${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `in ${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

export default function CampaignsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/campaigns/dashboard/summary`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!data) return null;

  const active = data.active_campaigns || [];
  const nextSends = data.next_sends || [];
  const monthlyPct = data.monthly_limit > 0
    ? Math.min(100, (data.monthly_sent / data.monthly_limit) * 100)
    : 0;
  const isOverBudget = data.overage_emails > 0;
  const barColor = isOverBudget ? "bg-red-500" : monthlyPct >= 80 ? "bg-amber-500" : "bg-blue-500";

  if (active.length === 0 && data.monthly_sent === 0) {
    return (
      <Card data-testid="campaigns-widget">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-blue-600" />
            AI Campaigns
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="py-4 text-center text-xs text-slate-500">
            <Sparkles className="w-6 h-6 mx-auto mb-2 text-slate-300" />
            No active campaigns. <Link to="/campaigns" className="text-blue-600 hover:underline">Launch your first drip →</Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="campaigns-widget">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base font-bold flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-blue-600" />
              AI Campaigns
            </CardTitle>
            <CardDescription className="text-xs">
              {active.length} active · {data.monthly_sent.toLocaleString()} emails this month
            </CardDescription>
          </div>
          <Link to="/campaigns" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5">
            Manage
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Hot replies alert */}
        {data.hot_replies > 0 && (
          <Link
            to="/campaigns/replies"
            className="flex items-center gap-2 p-2.5 rounded-md bg-green-50 border border-green-200 hover:bg-green-100"
            data-testid="hot-replies-link"
          >
            <Flame className="w-4 h-4 text-green-700 flex-shrink-0" />
            <div className="flex-1 text-xs">
              <span className="font-semibold text-green-900">{data.hot_replies} interested {data.hot_replies === 1 ? "reply" : "replies"}</span>
              <span className="text-green-700"> — campaign paused, reply personally</span>
            </div>
            <ArrowRight className="w-3 h-3 text-green-700" />
          </Link>
        )}
        {data.pending_replies > 0 && data.hot_replies === 0 && (
          <Link
            to="/campaigns/replies"
            className="flex items-center gap-2 p-2.5 rounded-md bg-blue-50 border border-blue-200 hover:bg-blue-100"
            data-testid="pending-replies-link"
          >
            <Inbox className="w-4 h-4 text-blue-700 flex-shrink-0" />
            <div className="flex-1 text-xs text-blue-900">
              <span className="font-semibold">{data.pending_replies} unread {data.pending_replies === 1 ? "reply" : "replies"}</span> awaiting review
            </div>
            <ArrowRight className="w-3 h-3 text-blue-700" />
          </Link>
        )}

        {/* Monthly send quota */}
        <div>
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-slate-600 font-medium">Monthly emails</span>
            <span className={`font-semibold ${isOverBudget ? "text-red-600" : monthlyPct >= 80 ? "text-amber-600" : "text-slate-700"}`}>
              {data.monthly_sent.toLocaleString()} / {data.monthly_limit.toLocaleString()}
            </span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full ${barColor} transition-all`} style={{ width: `${monthlyPct}%` }} />
          </div>
          {isOverBudget && (
            <p className="text-xs text-red-600 mt-1">
              {data.overage_emails.toLocaleString()} over · est. ${data.overage_cost.toFixed(2)} overage
            </p>
          )}
        </div>

        {/* Active campaigns list */}
        {active.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-slate-600">Running now</div>
            {active.map((c) => (
              <Link
                key={c.campaign_id}
                to="/campaigns"
                className="flex items-center justify-between p-2 rounded-md hover:bg-slate-50"
                data-testid={`widget-campaign-${c.campaign_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500">
                    {c.stats?.enrolled || 0} enrolled · {c.stats?.sent || 0} sent · {c.steps?.length || 0} steps
                  </div>
                </div>
                <Badge variant="secondary" className="text-[10px] uppercase">active</Badge>
              </Link>
            ))}
          </div>
        )}

        {/* Next scheduled sends */}
        {nextSends.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-slate-600 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Next sends
            </div>
            <div className="rounded-md border border-slate-200 divide-y divide-slate-100">
              {nextSends.slice(0, 3).map((e, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-slate-800 font-medium">
                      <Mail className="w-3 h-3 inline mr-1 text-slate-400" />
                      {e.lead_name}
                    </div>
                    <div className="text-slate-500 truncate">
                      {e.campaign_name} · step {(e.current_step || 0) + 1}
                    </div>
                  </div>
                  <span className="text-slate-500 whitespace-nowrap ml-2">
                    {formatRelative(e.next_send_at)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
